import { useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, MessageSquare } from "lucide-react";
import ReactMarkdown from "react-markdown";
import type { ChatMessage } from "@/types/chat";
import { ChatMessageBubble } from "./ChatMessageBubble";

interface ChatMessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  isReadOnly?: boolean;
  streamingContent?: string;
  streamingPersonaLabel?: string;
  messagePersonaLabels?: Record<number, string>;
  onFeedback?: (messageId: number, feedback: 1 | -1 | null) => void;
}

export function ChatMessageList({
  messages,
  isLoading,
  isReadOnly,
  streamingContent,
  streamingPersonaLabel,
  messagePersonaLabels,
  onFeedback,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, streamingContent]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 p-8">
        <MessageSquare className="w-12 h-12 opacity-30" />
        <p className="text-sm">
          {isReadOnly
            ? "No messages in this session."
            : "Start a conversation by sending a message."}
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1 px-4">
      <div className="space-y-4 py-4">
        {messages.map((msg) => (
          <ChatMessageBubble
            key={msg.id}
            message={msg}
            isReadOnly={isReadOnly}
            personaLabel={messagePersonaLabels?.[msg.id]}
            onFeedback={onFeedback}
          />
        ))}

        {isLoading && streamingContent && (
          <div className="flex gap-3 max-w-[85%] mr-auto">
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted text-muted-foreground">
              <Bot className="w-4 h-4" />
            </div>
            <div className="flex flex-col gap-1 items-start">
            {streamingPersonaLabel && (
              <span className="text-[10px] font-medium text-primary/70 px-1">{streamingPersonaLabel}</span>
            )}
            <div className="rounded-2xl rounded-bl-md bg-muted px-4 py-2.5 text-sm text-foreground">
              <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-headings:text-sm prose-p:leading-relaxed">
                <ReactMarkdown>{streamingContent}</ReactMarkdown>
                <span className="inline-block w-[2px] h-4 bg-foreground/70 animate-pulse ml-0.5 align-text-bottom" />
              </div>
            </div>
            </div>
          </div>
        )}

        {isLoading && !streamingContent && (
          <div className="flex gap-3 max-w-[85%] mr-auto">
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted text-muted-foreground">
              <Bot className="w-4 h-4" />
            </div>
            <div className="rounded-2xl rounded-bl-md bg-muted px-4 py-3">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
}
